import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, inject, Input, OnInit, TemplateRef } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  PasCheckboxComponent,
  PasConfirmationService,
  PasInputFieldComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { NgxSpinnerService } from 'ngx-spinner';

import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { NotificationServiceWrapper } from '../../../../services/notification-service-wrapper/notification-service-wrapper';
import { TableViewService } from '../../../../services/reporting/table-view.service';
import { GenericModalContentComponent } from '../../../../shared/generic-modal-content/generic-modal-content.component';
import { TableViewCustomModel } from '../common/model/table-view-custom-model';
import { TableViewCustomModelOption } from '../common/model/table-view-custom-model-option';
import {
  customTableViewNameValidatorOnEdit,
  customTableViewNameValidatorOnNew,
} from '../common/validator/custom-table-view-validator';
import { TvaCustomService } from '../tva-custom.service';

const CONST_ON_PROCESS_SAVE_NEW_TABLE_VIEW = 'save';
const CONST_ON_PROCESS_UPDATE_TABLE_VIEW = 'update';
const CONST_ON_PROCESS_DELETE_TABLE_VIEW = 'delete';

const CONST_REG_EXP_FOR_CUSTOM_TABLE_VIEW_NAME = '[a-zA-Z0-9äÄöÖüÜß]{1}[a-zA-Z0-9äÄöÖüÜß\\s]*';
const CONST_TABLE_VIEW_NAME_MAX_LENGTH = 50;

const NOTIFICATION_SAVE_TABLEVIEW_SUCCESS = 'notification_save_tableview_success';
const NOTIFICATION_SAVE_MISC_ERROR = 'notification_save_misc_error';
export const NOTIFICATION_UNEXPECTED_ERROR = 'notification_unexpected_error';
const NOTIFICATION_DELETE_MISC_ERROR = 'notification_delete_misc_error';
const NOTIFICATION_DELETE_MISC_SUCCESS = 'notification_delete_misc_success';

const CONST_SYSTEM_DEFAULT_TABLE_VIEW: TableViewCustomModelOption = null;

@Component({
  selector: 'app-tva-custom-sidebar',
  templateUrl: './tva-custom-sidebar.component.html',
  styleUrls: ['./tva-custom-sidebar.component.scss'],
  imports: [
    CommonModule,
    TranslatePipe,
    GenericModalContentComponent,
    FormsModule,
    ReactiveFormsModule,
    PasInputFieldComponent,
    PasCheckboxComponent,
  ],
})
export class TvaCustomSidebarComponent implements OnInit, AfterViewInit {
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(NgbModal);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly tableViewService = inject(TableViewService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly tvaCustomService = inject(TvaCustomService);
  private readonly pasConfirmationService = inject(PasConfirmationService);

  public formGroupNewTableView: UntypedFormGroup;
  private modalRefNewTableView: NgbModalRef;

  public readonly update = CONST_ON_PROCESS_UPDATE_TABLE_VIEW;
  public readonly save = CONST_ON_PROCESS_SAVE_NEW_TABLE_VIEW;

  @Input()
  public numberOfButtonsVisibleSidebar: number;

  private screenHeight: number;

  private readonly viewButtonsPixelSize = 380;
  private readonly buttonSize = 50;

  public numberOfEmptyRows: number;
  public formGroupEditTableView: UntypedFormGroup;
  public modalRefEditTableView: NgbModalRef;

  private readonly modalOptions: NgbModalOptions = {
    centered: true,
    backdrop: 'static',
    keyboard: false,
    size: 'lg',
  };

  public txtTableViewName: string;
  public chkIsDefaultView: boolean;
  public chkSaveModifiedTableMetadata: boolean;
  public labelTextForChkIsDefaultView: string;

  public isBtnResetDefaultTableViewDisabled = true;

  public showMessageModifiedTableMetadata = false;

  public constructor() {
    this.tvaCustomService.setTvaCustomSidebarComponent(this);
  }

  public ngOnInit(): void {
    this.doResetModelItems();
    this.getScreenSize();
  }

  public ngAfterViewInit(): void {
    // Validator for modalRefNewTableView
    this.formGroupNewTableView = this.fb.group({
      formControlTxtTableViewName: [
        '',
        [
          Validators.required,
          Validators.maxLength(CONST_TABLE_VIEW_NAME_MAX_LENGTH),
          Validators.pattern(CONST_REG_EXP_FOR_CUSTOM_TABLE_VIEW_NAME),
          (control: AbstractControl<any, any>) =>
            customTableViewNameValidatorOnNew(control, this.translateService, this.tvaCustomService),
        ],
      ],
      formControlChkIsDefaultView: [false],
    });

    // Validator for modalRefEditTableView
    this.formGroupEditTableView = this.fb.group({
      formControlTxtTableViewName: [
        '',
        [
          Validators.required,
          Validators.maxLength(CONST_TABLE_VIEW_NAME_MAX_LENGTH),
          Validators.pattern(CONST_REG_EXP_FOR_CUSTOM_TABLE_VIEW_NAME),
          (control: AbstractControl<any, any>) =>
            customTableViewNameValidatorOnEdit(control, this.translateService, this.tvaCustomService),
        ],
      ],
      formControlChkIsDefaultView: [false],
      formControlChkSaveModifiedTableMetadata: [false],
    });
  }

  public onBtnOpenModalNewTableView(modalNewTableView: TemplateRef<any>): void {
    this.doResetModelItems();
    this.modalRefNewTableView = this.modalService.open(modalNewTableView, this.modalOptions);
  }

  public onBtnOpenModalEditTableView(modalEditTableView: TemplateRef<any>): void {
    this.doResetModelItems();
    const selectedTableView: TableViewCustomModel = this.getSelectedTableView();
    this.txtTableViewName = selectedTableView.tableViewName;
    this.chkIsDefaultView = selectedTableView.defaultView;
    this.chkSaveModifiedTableMetadata = false;
    this.modalRefEditTableView = this.modalService.open(modalEditTableView, this.modalOptions);
  }

  public onBtnResetDefaultTableView(): void {
    this.tvaCustomService.doSelectedTableViewOnComponent();
  }

  public onBtnResetSystemDefaultTableView(): void {
    this.tvaCustomService.setSelectedTableViewOnComponent(CONST_SYSTEM_DEFAULT_TABLE_VIEW);
  }

  public onBtnDeleteSelectedTableView(): void {
    const title: string = this.translateService.instant('TABLE_VIEW_MODAL_DELETE_HEADER');
    const message: string = this.translateService.instant('TABLE_VIEW_MODAL_DELETE_MESSAGE', {
      value: this.getSelectedTableView().tableViewName,
    });
    const ok: string = this.translateService.instant('OK');
    const dismiss: string = this.translateService.instant('CANCEL');

    this.pasConfirmationService
      .showConfirmation(title, message, ok, dismiss, false)
      .then(() => {
        this.doSaveOrUpdateOrDeleteTableView(CONST_ON_PROCESS_DELETE_TABLE_VIEW);
        this.tvaCustomService.doSelectedTableViewOnComponent();
      })
      .catch(() => {
        // Cancel was clicked on confiramtion dialog
      });
  }

  public onBtnSaveTableView(): void {
    this.doSaveOrUpdateOrDeleteTableView(CONST_ON_PROCESS_SAVE_NEW_TABLE_VIEW);
  }

  public onBtnCancelNewTableView(): void {
    this.modalRefNewTableView.close();
    this.formGroupNewTableView.reset();
  }

  public onBtnUpdateTableView(): void {
    this.doSaveOrUpdateOrDeleteTableView(CONST_ON_PROCESS_UPDATE_TABLE_VIEW);
  }

  public onBtnCancelEditTableView(): void {
    this.modalRefEditTableView.close();
    this.formGroupEditTableView.reset();
  }

  public onChkIsDefaultViewOnModals(val: boolean, processMode: string): void {
    this.setLabelTextForChkIsDefaultView(val, processMode);
  }

  public onResize(): void {
    // Only if SystemDefault profile is selected, the resizing is executed.
    // For user-defined profiles, the sizes stored in the profile are retained.
    if (!this.getSelectedTableView()) {
      this.tvaCustomService.gridApi.sizeColumnsToFit();
    }
  }

  protected createArrayNumbersOfLength(n: number): number[] {
    return Array.from({ length: n }, (notUsedElement, i) => i);
  }

  private doResetModelItems(): void {
    this.txtTableViewName = null;
    this.chkIsDefaultView = false;
    this.chkSaveModifiedTableMetadata = false;
    this.labelTextForChkIsDefaultView = this.translateService.instant('REPORT_MODAL_DEFAULT_VIEW');
  }

  /**
   * These operations are collected in one method to execute further things after the processes
   */
  private doSaveOrUpdateOrDeleteTableView(processMode: string): void {
    switch (processMode) {
      case CONST_ON_PROCESS_DELETE_TABLE_VIEW: {
        this.deleteTableView();
        break;
      }
      case CONST_ON_PROCESS_UPDATE_TABLE_VIEW: {
        this.updateTableView();
        break;
      }
      case CONST_ON_PROCESS_SAVE_NEW_TABLE_VIEW: {
        this.saveTableView();
        break;
      }
      default: {
        break;
      }
    }
    this.tvaCustomService.doCheckResetDefaultTableViewActivability();
  }

  /**
   * Enable/disable Ok Button on modalEditTableView.
   * Without changes the button will be disabled.
   */
  public isBtnOkOnModalEditTableViewEnabled(): boolean {
    const selectedTableView: TableViewCustomModel = this.getSelectedTableView();
    const isPivotModeChanged: boolean = this.isPivotModeChanged(selectedTableView);
    const isColumnStateChanged: boolean = this.isColumnStateChanged(selectedTableView);
    const isFilterModelChanged: boolean = this.isFilterModelChanged(selectedTableView);

    this.showMessageModifiedTableMetadata = false;

    if (this.isTableViewNameAndDefaultViewUnchanged(selectedTableView)) {
      if (isPivotModeChanged || isFilterModelChanged || isColumnStateChanged) {
        this.showMessageModifiedTableMetadata = true;
        return this.chkSaveModifiedTableMetadata;
      }
    } else {
      if (isPivotModeChanged || isFilterModelChanged || isColumnStateChanged) {
        this.showMessageModifiedTableMetadata = true;
      }
      return this.isFormValidAndDirty(selectedTableView);
    }
    return false;
  }

  private isPivotModeChanged(selectedTableView: TableViewCustomModel): boolean {
    return selectedTableView.pivotMode != this.tvaCustomService.gridApi.isPivotMode();
  }

  private isColumnStateChanged(selectedTableView: TableViewCustomModel): boolean {
    return selectedTableView.columnState != JSON.stringify(this.tvaCustomService.gridApi.getColumnState());
  }

  private isFilterModelChanged(selectedTableView: TableViewCustomModel): boolean {
    return selectedTableView.filterModel != JSON.stringify(this.tvaCustomService.gridApi.getFilterModel());
  }

  private isTableViewNameAndDefaultViewUnchanged(selectedTableView: TableViewCustomModel): boolean {
    return (
      selectedTableView.tableViewName === this.txtTableViewName &&
      selectedTableView.defaultView === this.chkIsDefaultView
    );
  }

  private isFormValidAndDirty(selectedTableView: TableViewCustomModel): boolean {
    return (
      this.formGroupEditTableView.valid &&
      this.formGroupEditTableView.dirty &&
      (selectedTableView.tableViewName != this.txtTableViewName ||
        selectedTableView.defaultView != this.chkIsDefaultView)
    );
  }

  public getSelectedTableView(): TableViewCustomModelOption {
    return this.tvaCustomService.getSelectedTableView();
  }

  private getDefaultTableView(): TableViewCustomModel {
    return this.tvaCustomService.getDefaultTableView();
  }

  private getTableViewByName(tableViewName: string): TableViewCustomModelOption {
    return this.tvaCustomService.tableViewOptions.find(
      tableViewCustomModelOption => tableViewCustomModelOption.tableViewName.trim() === tableViewName.trim(),
    );
  }

  private setLabelTextForChkIsDefaultView(val: boolean, processMode: string): void {
    const defaultTableView: TableViewCustomModel = this.getDefaultTableView();

    if (processMode === CONST_ON_PROCESS_SAVE_NEW_TABLE_VIEW && defaultTableView != undefined && val) {
      this.labelTextForChkIsDefaultView = this.translateService.instant('TABLE_VIEW_MODAL_CONFIRMATION_MESSAGE', {
        value: defaultTableView.tableViewName,
      });
    } else if (
      processMode === CONST_ON_PROCESS_UPDATE_TABLE_VIEW &&
      defaultTableView != undefined &&
      defaultTableView.id != this.getSelectedTableView().id &&
      val
    ) {
      this.labelTextForChkIsDefaultView = this.translateService.instant('TABLE_VIEW_MODAL_CONFIRMATION_MESSAGE', {
        value: defaultTableView.tableViewName,
      });
    } else {
      //  defaultTableView is undefined -> No item available -> save the new item
      this.labelTextForChkIsDefaultView = this.translateService.instant('TABLE_VIEW_MODAL_DEFAULT_VIEW');
    }
  }

  private saveTableView(): void {
    this.modalRefNewTableView.close();
    this.spinner.show();
    this.tableViewService
      .saveTableView(
        new TableViewCustomModel(
          this.txtTableViewName,
          this.chkIsDefaultView,
          this.tvaCustomService.isTableWithPivotFunctionality == true
            ? this.tvaCustomService.gridApi.isPivotMode()
            : false,
          JSON.stringify(this.tvaCustomService.gridApi.getColumnState()),
          JSON.stringify(this.tvaCustomService.gridApi.getFilterModel()),
          this.tvaCustomService.tableViewAreaEnumVal,
        ),
      )
      .subscribe({
        next: resultList => {
          this.showNotification(NOTIFICATION_SAVE_TABLEVIEW_SUCCESS, this.txtTableViewName);
          this.tvaCustomService.doMapTableView(resultList);
          this.tvaCustomService.setSelectedTableViewOnComponent(this.getTableViewByName(this.txtTableViewName));
          this.formGroupNewTableView.reset('');
          this.spinner.hide();
        },
        error: () => {
          this.showNotification(NOTIFICATION_SAVE_MISC_ERROR, this.txtTableViewName);
          this.spinner.hide();
        },
      });
  }

  private deleteTableView(): void {
    const selectedTableView: TableViewCustomModelOption = this.getSelectedTableView();

    this.spinner.show();
    this.tableViewService.deleteTableView(selectedTableView).subscribe({
      next: resultList => {
        this.showNotification(NOTIFICATION_DELETE_MISC_SUCCESS, selectedTableView.tableViewName);
        this.tvaCustomService.doMapTableView(resultList);
        this.tvaCustomService.doSelectedTableViewOnComponent();
        this.spinner.hide();
      },
      error: () => {
        this.showNotification(NOTIFICATION_DELETE_MISC_ERROR, selectedTableView.tableViewName);
        this.tvaCustomService.setSelectedTableViewOnComponent(selectedTableView);
        this.spinner.hide();
      },
    });
  }

  /**
   * Update/Edit a tableView.
   * Currently, only the name and/or the DefaultFlag will considered.
   * Changes on tableSorting, tableOrdering ect. will not get saved -> we stay by the old one.
   */
  private updateTableView(): void {
    this.modalRefEditTableView.close();

    const selectedTableView: TableViewCustomModel = this.getSelectedTableView();
    selectedTableView.tableViewName = this.txtTableViewName;
    selectedTableView.defaultView = this.chkIsDefaultView;

    if (this.chkSaveModifiedTableMetadata) {
      selectedTableView.pivotMode = this.tvaCustomService.gridApi.isPivotMode();
      selectedTableView.columnState = JSON.stringify(this.tvaCustomService.gridApi.getColumnState());
      selectedTableView.filterModel = JSON.stringify(this.tvaCustomService.gridApi.getFilterModel());
    }

    this.spinner.show();
    this.tableViewService.updateTableView(selectedTableView).subscribe({
      next: resultList => {
        this.showNotification(NOTIFICATION_SAVE_TABLEVIEW_SUCCESS, this.txtTableViewName);
        this.tvaCustomService.doMapTableView(resultList);
        // Set new table view as selected view
        this.tvaCustomService.setSelectedTableViewOnComponent(this.getTableViewByName(this.txtTableViewName));
        this.formGroupEditTableView.reset();
        this.spinner.hide();
      },
      error: () => {
        this.showNotification(NOTIFICATION_SAVE_MISC_ERROR, this.txtTableViewName);
        this.spinner.hide();
      },
    });
  }

  public showNotification(type: string, message: string): void {
    // eslint-disable-next-line default-case
    switch (type) {
      case NOTIFICATION_SAVE_TABLEVIEW_SUCCESS: {
        this.notificationServiceWrapper.addSuccessNotification(
          this.translateService.instant('SAVED_TITLE'),
          `${message} - ${this.translateService.instant('SAVED_SUCCESSFUL')}`,
        );
        break;
      }
      case NOTIFICATION_SAVE_MISC_ERROR: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_SAVE_ERROR_HEADER'),
          `${message} - ${this.translateService.instant('NOTIFICATION_SAVE_ERROR_MESSAGE')}`,
        );
        break;
      }
      case NOTIFICATION_UNEXPECTED_ERROR: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_HEADER'),
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_MESSAGE'),
        );
        break;
      }
      case NOTIFICATION_DELETE_MISC_ERROR: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_DELETE_MISC_ERROR_HEADER'),
          `${message} - ${this.translateService.instant('NOTIFICATION_DELETE_MISC_ERROR_MESSAGE')}`,
        );
        break;
      }
      case NOTIFICATION_DELETE_MISC_SUCCESS: {
        this.notificationServiceWrapper.addSuccessNotification(
          this.translateService.instant('NOTIFICATION_DELETE_MISC_SUCCESS_HEADER'),
          `${message} - ${this.translateService.instant('NOTIFICATION_DELETE_MISC_SUCCESS_MESSAGE')}`,
        );
        break;
      }
    }
  }

  // adding whitespace between two groups of buttons in sidebar
  // depedning of how many buttons is vissible in first group we send parameter to remove adding to many whitespaces
  @HostListener('window:resize')
  public getScreenSize(): void {
    // eslint-disable-next-line no-restricted-globals
    this.screenHeight = window.innerHeight;
    const remainingPixels =
      this.screenHeight - this.numberOfButtonsVisibleSidebar * this.buttonSize - this.viewButtonsPixelSize;
    if (remainingPixels > 0) {
      // 21 is height of empty row
      this.numberOfEmptyRows = Math.floor(remainingPixels / 21);
    }
  }
}
